import { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { useStateMachine } from "little-state-machine";
import { disableBodyScroll, enableBodyScroll, clearAllBodyScrollLocks } from "body-scroll-lock";
import { Form } from "@components/Form";
import { SimpleBlockContent } from "@components/SimpleBlockContent";
import { useMobileDetect } from "@hooks/useMobileDetect";
import { Summary } from "./Summary";
import { Selection } from "./Selection";
import { Details } from "./Details";
import { Footer } from "./Footer";
import { StockWarning } from "./StockWarning";
import { isBra } from "../lib/isBra";
import { getSize } from "../lib/product-utils";
import { hasComingSoon } from "../lib/has-coming-soon";
import { resolveHasSizeData } from "../SizeGuide/lib/resolve-has-data";
import { SizeGuide } from "..";
import { useForm } from "./hooks/useForm";
import styles from "./styles/info.module.css";

export function Sidebar({ settings, openReviewsDrawer, scrollToReviews }) {
  const [showSizeGuide, setShowSizeGuide] = useState(false);
  const [targetElement, setTargetElement] = useState(null);
  const sizeGuideRef = useRef(null);
  const currentDevice = useMobileDetect();
  const { state } = useStateMachine();
  const {
    product,
    price,
    compareAtPrice,
    amount,
    navigation,
    options,
    finalSale,
    handleOptionChange,
    handleNavigationChange,
    handleSubmit,
  } = useForm();

  useEffect(() => {}, [price]);

  useEffect(() => {
    setTargetElement(sizeGuideRef.current);
    return () => {
      clearAllBodyScrollLocks();
    };
  }, []);

  function closeSizeGuide() {
    setShowSizeGuide(false);
    if (currentDevice.isMobile()) {
      enableBodyScroll(targetElement);
    }
  }

  function openSizeGuide() {
    setShowSizeGuide(true);
    if (currentDevice.isMobile()) {
      disableBodyScroll(targetElement);
    }
  }

  return (
    <>
      {state["productGroup"].sizeGuide && (
        <SizeGuide
          product={product}
          variant={state[`variant`]}
          data={state[`productGroup`].sizeGuide}
          isBra={isBra(state[`productGroup`].slug)}
          showSizeGuide={showSizeGuide}
          closeSizeGuide={closeSizeGuide}
          selectedSize={getSize(state[`variant`])}
          sizes={options.get("Size")}
          handleOptionChange={handleOptionChange}
          addToCart={handleSubmit}
          productTitle={state[`productGroup`].title.replace(/(Wo)?[Mm]en['’]?s /g, "")}
          comingSoon={hasComingSoon(state[`productGroup`], state["product"].id)}
          waitlistMessage={product.waitlistMessage}
          ref={sizeGuideRef}
        />
      )}
      <div className={styles.wrapper}>
        <Form onSubmit={handleSubmit} noValidate>
          <div className={styles.formBody}>
            <Summary
              price={price}
              amount={amount}
              compareAtPrice={compareAtPrice}
              finalSale={finalSale.includes(navigation.find((prod) => prod.selected)?.id)}
              description={<SimpleBlockContent blocks={state[`productGroup`].description} />}
              openReviewsDrawer={openReviewsDrawer}
              scrollToReviews={scrollToReviews}
            />
            <Selection
              navigation={navigation}
              options={options}
              product={product}
              handleNavigationChange={handleNavigationChange}
              handleOptionChange={handleOptionChange}
              openSizeGuide={
                resolveHasSizeData(state[`productGroup`].sizeGuide) ? openSizeGuide : null
              }
            />

            {!product.productReferenceV2.tags.includes("pre-launch") && (
              <StockWarning variant={state[`variant`]} />
            )}

            <Details settings={settings} />
          </div>
          <Footer
            addToCart={handleSubmit}
            comingSoon={hasComingSoon(state[`productGroup`], state["product"].id)}
            showWaitlist={product.productReferenceV2?.tags.includes("waitlist") || product.productReferenceV2?.tags.includes("pre-launch")}
            waitlistMessage={product.waitlistMessage}
          />
        </Form>
      </div>
    </>
  );
}

Sidebar.propTypes = { openReviewsDrawer: PropTypes.func, settings: PropTypes.object };
